import type { Icon } from "@phosphor-icons/react";
import {
	ArrowsOutCardinal,
	BracketsCurly,
	Browser,
	ClosedCaptioning,
	CursorClick,
	FilmSlate,
	Gif,
	Gradient,
	GridFour,
	Sparkle,
} from "@phosphor-icons/react";

interface FeatureItem {
	icon: Icon;
	title: string;
	description: string;
	kicker: string;
	href?: string;
	linkLabel?: string;
}

interface FeaturesProps {
	headingPrefix: string;
	headingAccent: string;
	description: string;
}

const FEATURES: FeatureItem[] = [
	{
		icon: CursorClick,
		kicker: "Attention control",
		title: "Cursor spotlight and click effects",
		description:
			"Guide the viewer through tutorials, demos, and bug reports with clearer cursor emphasis and click feedback.",
	},
	{
		icon: ArrowsOutCardinal,
		kicker: "Focus transitions",
		title: "Auto-zoom that follows the action",
		description:
			"Generate zoom regions from click activity so the important detail stays framed without extra manual editing.",
	},
	{
		icon: Browser,
		kicker: "Presentation polish",
		title: "Device and browser framing",
		description:
			"Wrap recordings in browser, laptop, tablet, or phone frames when the story needs more structure than a raw capture.",
	},
	{
		icon: FilmSlate,
		kicker: "Fast post-production",
		title: "Trim, speed up, and ship in one editor",
		description:
			"Refine a take without exporting into a second tool just to clean timing, cut dead space, or tighten pacing.",
	},
	{
		icon: Gradient,
		kicker: "Brandable output",
		title: "Backgrounds that feel intentional",
		description:
			"Use gradients, wallpapers, and custom backdrops to make async updates feel like finished communication, not leftovers.",
	},
	{
		icon: BracketsCurly,
		kicker: "Explanatory overlays",
		title: "Annotations for complex workflows",
		description:
			"Add text, arrows, and visual markers when a workflow needs explanation beyond cursor movement alone.",
	},
	{
		icon: Gif,
		kicker: "Distribution",
		title: "Export MP4 and GIF from the same flow",
		description:
			"Publish tutorials, release notes, bug reports, and social clips in the format that fits the destination.",
	},
	{
		icon: ClosedCaptioning,
		kicker: "Private accessibility",
		title: "On-device captions and subtitle export",
		description:
			"Generate a caption draft locally, review it, burn subtitles into the video, or export SRT and VTT without uploading the recording.",
		href: "/blog/how-to-add-captions-to-a-screen-recording/",
		linkLabel: "How to add captions to a screen recording",
	},
	{
		icon: GridFour,
		kicker: "Repeatable workflows",
		title: "Keyboard-driven editing and export",
		description:
			"Move quickly through recording, cleanup, and export with shortcuts that support frequent, repeatable production.",
	},
];

function FeatureBlock({ feature, className }: { feature: FeatureItem; className?: string }) {
	const Icon = feature.icon;

	return (
		<article className={`glass-card h-full p-6 md:p-7 ${className ?? ""}`}>
			<div className="flex items-start justify-between gap-6">
				<div>
					<p className="text-[11px] uppercase tracking-[0.18em] text-white/35">{feature.kicker}</p>
					<h3 className="mt-3 max-w-sm text-xl font-semibold tracking-tight text-white/90">
						{feature.title}
					</h3>
				</div>
				<div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/[0.08] bg-brand-500/10 text-brand-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
					<Icon size={20} weight="regular" />
				</div>
			</div>
			<p className="mt-6 max-w-[42ch] text-sm leading-7 text-white/52">{feature.description}</p>
			{feature.href && feature.linkLabel ? (
				<a
					href={feature.href}
					className="mt-4 inline-flex text-sm font-medium text-brand-300 underline decoration-brand-300/30 underline-offset-2 transition-colors hover:text-brand-200"
				>
					{feature.linkLabel} <span aria-hidden="true">→</span>
				</a>
			) : null}
		</article>
	);
}

export function Features({ headingPrefix, headingAccent, description }: FeaturesProps) {
	return (
		<section id="features" className="py-24 md:py-32">
			<div className="mx-auto max-w-6xl px-6">
				<div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
					<div className="lg:sticky lg:top-24">
						<p className="text-[11px] uppercase tracking-[0.22em] text-brand-300">Feature set</p>
						<h2 className="mt-4 max-w-md text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
							{headingPrefix} <span className="text-white/58">{headingAccent}</span>
						</h2>
						<p className="mt-5 max-w-[34rem] text-base leading-8 text-white/50 md:text-lg">
							{description}
						</p>
						<div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-sm text-white/60">
							<Sparkle size={16} weight="fill" className="text-brand-300" />
							Built for demos, tutorials, and async updates
						</div>
					</div>

					<div className="grid gap-4 md:grid-cols-2">
						<FeatureBlock feature={FEATURES[0]} className="md:translate-y-10" />
						<FeatureBlock feature={FEATURES[1]} />
						<FeatureBlock feature={FEATURES[2]} />
						<FeatureBlock feature={FEATURES[3]} className="md:-translate-y-6" />
						<FeatureBlock feature={FEATURES[4]} className="md:-translate-y-4" />
						<FeatureBlock feature={FEATURES[5]} className="md:translate-y-8" />
						<FeatureBlock feature={FEATURES[6]} />
						<FeatureBlock feature={FEATURES[7]} className="md:translate-y-4" />
						<FeatureBlock feature={FEATURES[8]} />
					</div>
				</div>
			</div>
		</section>
	);
}
